import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FiEdit2, FiTrash2, FiClock, FiEye } from "react-icons/fi";
import MediaBlock from "./MediaBlock";
import config from "../../../config";

const FILE_URL = config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL;

const VaccinesForm = ({ pet, onEdit, onDelete }) => {
  const [timeline, setTimeline] = useState([]);
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (pet?._id) {
      loadAllActivities(pet._id);
    }
  }, [pet?._id]);

  const loadAllActivities = async (petId) => {
    try {
      const [diaryRes, bookingRes] = await Promise.all([
        axios.get(
          `${config.API_BASE_URL}/customer/getDiaryByPet/${petId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${config.API_BASE_URL}/customer/getBookingActivies/${petId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
      ]);

      const diaryData = diaryRes.data?.data || [];
      const bookingData = bookingRes.data?.data || [];

      const merged = [
        ...normalizeDiary(diaryData),
        ...normalizeBooking(bookingData),
      ].sort((a, b) => new Date(b.date) - new Date(a.date));

      setTimeline(merged);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load pet activities");
    }
  };

  const parseDate = (dateStr) => {
    if (!dateStr) return null;

    // Handle DD/MM/YYYY
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      const [dd, mm, yyyy] = dateStr.split("/");
      return new Date(`${yyyy}-${mm}-${dd}`);
    }

    // Handle ISO string
    const isoDate = new Date(dateStr);
    return isNaN(isoDate) ? null : isoDate;
  };

  const normalizeDiary = (data) =>
    data.map((d) => {
      const date = parseDate(d.createdAt);
      return {
        id: d._id,
        type: "DIARY",
        title: d.title,
        category: d.category,
        notes: d.notes,
        time: date
          ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
          : "--",
        visibility: d.visibility,
        date,
        media: d.media || [],
      };
    });

  const normalizeBooking = (data) =>
    data.map((b) => {
      const activity = b.activity || {};
      const date = parseDate(activity.date) || parseDate(b.createdAt);

      const basePhotos = (b.photos || []).map((p) => `${FILE_URL}/${p}`);
      const baseVideos = (b.videos || []).map((v) => `${FILE_URL}/${v}`);
      const groomingPhotos = Object.values(activity.groomingSelections || {})
        .flatMap((grp) => Object.values(grp || {}))
        .flatMap((opt) => opt?.photos?.map((p) => `${FILE_URL}/${p}`) || []);

      const isVetClinic = !!activity.veterinarian;

      return {
        id: b._id,
        type: "BOOKING",
        title: isVetClinic
          ? `Vet Visit – ${activity.clinic || "Clinic"}`
          : activity.activity || "Service Activity",
        category: "Service Activity Update",
        notes: activity.notes || activity.instructions || "",
        time: date
          ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
          : "--",
        date,
        media: [...basePhotos, ...baseVideos, ...groomingPhotos],
        vendor: b.vendorDetails?.companyName || b.vendorDetails?.name || "",
        meta: {
          tests: activity.tests || [],
          medications: activity.medications || [],
          vitals: activity.vitals || null,
          veterinarian: activity.veterinarian || "",
          prescription: activity.prescriptionFile || "",
        },
      };
    });

  if (!timeline.length) {
    return (
      <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-4">
        <div className="w-20 h-20 sm:w-24 sm:h-24 mb-6 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
          <span className="text-4xl sm:text-5xl">🐾</span>
        </div>
        <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">
          No Activity Found
        </h3>
        <p className="text-gray-500 text-center max-w-md text-sm sm:text-base">
          Start tracking your pet's journey by adding diary entries or booking
          services
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="relative">
        <div className="absolute left-7 top-12 bottom-8 w-0.5 bg-gradient-to-b from-blue-200 via-purple-200 to-pink-200 opacity-70 md:left-10"></div>

        <div className="space-y-8 sm:space-y-10">
          {timeline.map((entry) => {
            const createdDate = entry.date || new Date();
            const day = createdDate.toLocaleDateString("en-US", {
              weekday: "short",
            });
            const dateNum = createdDate.getDate();
            const month = createdDate.toLocaleDateString("en-US", {
              month: "short",
            });
            const year = createdDate.getFullYear();

            const categoryColors = {
              DIARY: "bg-blue-100 text-blue-700 border-blue-200",
              "Service Update": "bg-purple-100 text-purple-700 border-purple-200",
              default: "bg-gray-100 text-gray-700 border-gray-200",
            };
            const colorClass =
              categoryColors[entry.category] || categoryColors.default;

            return (
              <div
                key={entry.id}
                className="relative flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6"
              >
                <div className="flex-shrink-0 relative z-10 sm:self-start">
                  <div className="w-14 sm:w-16 md:w-20 bg-white border-2 border-gray-200 rounded-xl p-2 md:p-3 text-center shadow-sm">
                    <div className="text-[10px] sm:text-xs font-semibold text-gray-500 uppercase">
                      {day}
                    </div>
                    <div className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">
                      {dateNum}
                    </div>
                    <div className="text-[10px] sm:text-xs font-medium text-gray-600">
                      {month}
                    </div>
                    <div className="text-[10px] text-gray-400">{year}</div>
                  </div>
                </div>

                <div className="flex-1 bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all border border-gray-100 overflow-hidden pb-6 sm:pb-0">
                  <div className="px-4 sm:px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold border ${colorClass}`}
                        >
                          {entry.category}
                        </span>

                        {entry.type === "BOOKING" && entry.vendor && (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                            {entry.vendor}
                          </span>
                        )}
                      </div>

                      {entry.type === "DIARY" && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => onEdit?.(entry)}
                            className="p-2 hover:bg-blue-50 rounded-lg transition"
                          >
                            <FiEdit2 className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            onClick={() => onDelete?.(entry.id)}
                            className="p-2 hover:bg-red-50 rounded-lg transition"
                          >
                            <FiTrash2 className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="px-4 sm:px-6 py-5 space-y-4">
                    {entry.title && (
                      <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-800">
                        {entry.title}
                      </h3>
                    )}

                    {entry.notes && (
                      <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                        {entry.notes}
                      </p>
                    )}

                    {entry.media?.length > 0 && (
                      <div className="pt-2">
                        <MediaBlock media={entry.media} />
                      </div>
                    )}
                  </div>

                  <div className="px-4 sm:px-6 py-3 border-t border-gray-100 bg-gray-50">
                    <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm text-gray-600">
                      <div className="flex items-center gap-1.5">
                        <FiClock className="w-4 h-4" />
                        <span>{entry.time}</span>
                      </div>

                      {entry.type === "DIARY" && entry.visibility && (
                        <div className="flex items-center gap-1.5">
                          <FiEye className="w-4 h-4" />
                          <span className="capitalize">{entry.visibility}</span>
                        </div>
                      )}

                      <span
                        className={`ml-auto px-2 py-1 rounded text-xs font-medium ${
                          entry.type === "DIARY"
                            ? "bg-blue-50 text-blue-700"
                            : "bg-purple-50 text-purple-700"
                        }`}
                      >
                        {entry.type === "DIARY"
                          ? "Personal Entry"
                          : "Service Record"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default VaccinesForm;
